import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Icons } from '../components/Icons';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';

function ZonePriceList() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [zones, setZones] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [groupedInventory, setGroupedInventory] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState(''); // New state for search query
  const [viewMode, setViewMode] = useState('all'); // 'zone' or 'all' - 'all' is default
  const [loading, setLoading] = useState(true);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, src: '', productName: '' });
  const [inventoryPopup, setInventoryPopup] = useState({ show: false, item: null, position: { x: 0, y: 0 } });
  const [productDetailModal, setProductDetailModal] = useState({ show: false, item: null }); // 제품 상세 모달

  // 견적서 관련 상태
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [customers, setCustomers] = useState([]);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [quotationQuantities, setQuotationQuantities] = useState({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // 거래처 등록 관련 상태
  const [showCustomerAddForm, setShowCustomerAddForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    type: '고객',
    contact: '',
    email: '',
    address: '',
    notes: ''
  });

  // 할인 관련 상태
  const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'amount'
  const [discountValue, setDiscountValue] = useState(0);
  const [showDiscountInQuotation, setShowDiscountInQuotation] = useState(true);

  const quotationRef = useRef();

  useEffect(() => {
    loadLocationsAndZones();
    loadCustomers();
  }, []);

  useEffect(() => {
    if (viewMode === 'all') {
      loadAllZoneInventory();
    } else if (selectedZone) {
      loadZoneInventory(selectedZone);
    } else {
      setInventory([]);
      setGroupedInventory({});
    }
    // 필터 변경 시 선택 초기화
    setSelectedItems(new Set());
    setCurrentPage(1); // Reset page on filter change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZone, viewMode, zones]);

  // 거래처 로드
  const loadCustomers = async () => {
    try {
      const customersRef = collection(db, 'customers');
      const snapshot = await getDocs(customersRef);
      const customerList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customerList);
    } catch (error) {
      console.error('거래처 로드 오류:', error);
    }
  };

  const loadLocationsAndZones = async () => {
    try {
      setLoading(true);

      const locationsRef = collection(db, 'locations');
      const locSnapshot = await getDocs(locationsRef);
      const locationList = locSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(loc => loc.active !== false)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setLocations(locationList);

      const defaultLocation = locationList.find(loc => loc.name === '자곡동 매장');
      if (defaultLocation) {
        setSelectedLocation(defaultLocation.id);
      }

      const zonesRef = collection(db, 'zones');
      const zoneSnapshot = await getDocs(zonesRef);
      const zoneList = zoneSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(zone => zone.active !== false && zone.isDisplay)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setZones(zoneList);
    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllZoneInventory = async () => {
    try {
      setLoadingInventory(true);

      const inventoryRef = collection(db, 'inventory');
      const allInventorySnapshot = await getDocs(
        query(inventoryRef, where('status', '!=', '판매완료'))
      );

      const grouped = {};
      const targetZones = selectedLocation
        ? zones.filter(z => z.locationId === selectedLocation)
        : zones;

      targetZones.forEach(zone => {
        const zoneName = `${zone.locationName} - ${zone.name}`;
        const zoneItems = [];

        allInventorySnapshot.docs.forEach(doc => {
          const item = { id: doc.id, ...doc.data() };

          if (item.zoneId === zone.id || item.zoneName === zoneName) {
            zoneItems.push({ ...item, zoneSource: 'product' });
          } else if (item.skuLocations && Array.isArray(item.skuLocations)) {
            const skusInZone = item.skuLocations.filter(
              sku => sku.zoneId === zone.id || sku.zoneName === zoneName
            );
            if (skusInZone.length > 0) {
              zoneItems.push({
                ...item,
                skuCount: skusInZone.length,
                totalSkuCount: item.skuLocations.length,
                zoneSource: 'sku'
              });
            }
          }
        });

        if (zoneItems.length > 0) {
          grouped[zone.name] = {
            zone,
            items: zoneItems.sort((a, b) =>
              (a.productName || '').localeCompare(b.productName || '')
            )
          };
        }
      });

      setGroupedInventory(grouped);
    } catch (error) {
      console.error('재고 로드 오류:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const loadZoneInventory = async (zoneId) => {
    try {
      setLoadingInventory(true);

      const selectedZoneInfo = zones.find(z => z.id === zoneId);
      const zoneName = selectedZoneInfo ? `${selectedZoneInfo.locationName} - ${selectedZoneInfo.name}` : '';

      const inventoryRef = collection(db, 'inventory');
      const allInventorySnapshot = await getDocs(
        query(inventoryRef, where('status', '!=', '판매완료'))
      );

      const seenIds = new Set();
      const allItems = [];

      allInventorySnapshot.docs.forEach(doc => {
        const item = { id: doc.id, ...doc.data() };

        if (seenIds.has(item.id)) return;

        if (item.zoneId === zoneId || item.zoneName === zoneName) {
          seenIds.add(item.id);
          allItems.push({ ...item, zoneSource: 'product' });
          return;
        }

        if (item.skuLocations && Array.isArray(item.skuLocations)) {
          const skusInZone = item.skuLocations.filter(
            sku => sku.zoneId === zoneId || sku.zoneName === zoneName
          );
          if (skusInZone.length > 0) {
            seenIds.add(item.id);
            allItems.push({
              ...item,
              skuCount: skusInZone.length,
              totalSkuCount: item.skuLocations.length,
              zoneSource: 'sku'
            });
          }
        }
      });

      allItems.sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
      setInventory(allItems);
    } catch (error) {
      console.error('재고 로드 오류:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const filteredZones = selectedLocation
    ? zones.filter(zone => zone.locationId === selectedLocation)
    : zones;

  const selectedZoneData = zones.find(z => z.id === selectedZone);
  const selectedZoneName = selectedZoneData
    ? `${selectedZoneData.locationName} - ${selectedZoneData.name}`
    : '';

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  const formatPriceWithTax = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('ko-KR').format(Math.round(price * 1.1));
  };

  // 체크박스 선택 관련 함수
  const getAllItems = () => {
    if (viewMode === 'all') {
      return Object.values(groupedInventory).flatMap(g => g.items);
    }
    return inventory;
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getSelectedItemsData = () => {
    const allItems = getAllItems();
    return allItems.filter(item => selectedItems.has(item.id));
  };

  // 재고 위치별 팝업 열기
  const handleInventoryClick = (e, item) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setInventoryPopup({
      show: true,
      item,
      position: { x: rect.left, y: rect.bottom + 5 }
    });
  };

  // 위치별 재고 계산
  const getLocationInventory = (item) => {
    if (!item.skuLocations || item.skuLocations.length === 0) {
      return [{ location: item.zoneName || '미지정', count: item.quantity || 1 }];
    }

    const locationMap = {};
    item.skuLocations.forEach(sku => {
      // location 필드가 실제 SKU 위치 데이터에 사용됨
      const locName = sku.location || sku.zoneName || sku.locationName || '미지정';
      if (!locationMap[locName]) {
        locationMap[locName] = 0;
      }
      locationMap[locName] += 1;
    });

    return Object.entries(locationMap).map(([location, count]) => ({ location, count }));
  };

  // 견적서 내 수량 변경
  const handleQuantityChange = (itemId, newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    setQuotationQuantities(prev => ({
      ...prev,
      [itemId]: qty
    }));
  };

  // 거래처 등록
  const handleAddCustomer = async () => {
    if (!newCustomerData.name.trim()) {
      alert('거래처 이름을 입력하세요.');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'customers'), {
        ...newCustomerData,
        createdAt: serverTimestamp()
      });

      const newCustomer = {
        id: docRef.id,
        ...newCustomerData
      };

      setCustomers(prev => [...prev, newCustomer]);
      setSelectedCustomer(newCustomer);
      setShowCustomerAddForm(false);
      setNewCustomerData({
        name: '',
        type: '고객',
        contact: '',
        email: '',
        address: '',
        notes: ''
      });
      alert('거래처가 등록되었습니다.');
    } catch (error) {
      console.error('거래처 등록 오류:', error);
      alert('거래처 등록 중 오류가 발생했습니다.');
    }
  };

  // 견적서 인쇄
  const handlePrintQuotation = useReactToPrint({
    contentRef: quotationRef,
    documentTitle: `견적서_${selectedCustomer?.name || ''}${new Date().toLocaleDateString('ko-KR').replace(/\./g, '')}`,
  });

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    const selectedData = getSelectedItemsData();
    const totals = calculateTotals();

    const excelData = selectedData.map((item, index) => {
      const qty = quotationQuantities[item.id] || item.quantity || 1;
      return {
        'No.': index + 1,
        '제품명': item.productName || '-',
        '디자이너': item.designer || '-',
        '제조사': item.manufacturer || '-',
        '재질': item.material || '-',
        '연대': item.era || '-',
        '사이즈': item.size || '-',
        '수량': qty,
        '단가(VAT미포함)': item.salePrice || 0,
        '단가(VAT포함)': Math.round((item.salePrice || 0) * 1.1),
        '금액(VAT미포함)': (item.salePrice || 0) * qty,
        '금액(VAT포함)': Math.round((item.salePrice || 0) * 1.1) * qty,
      };
    });

    // 소계 행 추가
    excelData.push({
      'No.': '',
      '제품명': '소계',
      '디자이너': '',
      '제조사': '',
      '재질': '',
      '연대': '',
      '사이즈': '',
      '수량': totals.totalQuantity,
      '단가(VAT미포함)': '',
      '단가(VAT포함)': '',
      '금액(VAT미포함)': totals.subtotalExclVat,
      '금액(VAT포함)': totals.subtotalInclVat,
    });

    // 할인이 있으면 할인 행 추가
    if (showDiscountInQuotation && totals.discountAmount > 0) {
      excelData.push({
        'No.': '',
        '제품명': `할인 (${discountType === 'percent' ? discountValue + '%' : formatPrice(discountValue) + '원'})`,
        '디자이너': '',
        '제조사': '',
        '재질': '',
        '연대': '',
        '사이즈': '',
        '수량': '',
        '단가(VAT미포함)': '',
        '단가(VAT포함)': '',
        '금액(VAT미포함)': -totals.discountAmount,
        '금액(VAT포함)': -Math.round(totals.discountAmount * 1.1),
      });
    }

    // 합계 행 추가
    excelData.push({
      'No.': '',
      '제품명': '합계',
      '디자이너': '',
      '제조사': '',
      '재질': '',
      '연대': '',
      '사이즈': '',
      '수량': totals.totalQuantity,
      '단가(VAT미포함)': '',
      '단가(VAT포함)': '',
      '금액(VAT미포함)': totals.totalExclVat,
      '금액(VAT포함)': totals.totalInclVat,
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '견적서');

    const fileName = `견적서_${selectedCustomer?.name || ''}${new Date().toLocaleDateString('ko-KR').replace(/\./g, '')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // 견적서 합계 계산
  const calculateTotals = () => {
    const selectedData = getSelectedItemsData();
    const subtotalExclVat = selectedData.reduce((sum, item) => {
      const qty = quotationQuantities[item.id] || item.quantity || 1;
      return sum + (item.salePrice || 0) * qty;
    }, 0);
    const totalQuantity = selectedData.reduce((sum, item) => {
      const qty = quotationQuantities[item.id] || item.quantity || 1;
      return sum + qty;
    }, 0);

    // 할인 계산
    let discountAmount = 0;
    if (discountValue > 0) {
      if (discountType === 'percent') {
        discountAmount = Math.round(subtotalExclVat * (discountValue / 100));
      } else {
        discountAmount = discountValue;
      }
    }

    const totalExclVat = subtotalExclVat - discountAmount;
    const subtotalInclVat = Math.round(subtotalExclVat * 1.1);
    const totalInclVat = Math.round(totalExclVat * 1.1);

    return {
      subtotalExclVat,
      subtotalInclVat,
      discountAmount,
      totalExclVat,
      totalInclVat,
      totalQuantity,
      itemCount: selectedData.length
    };
  };

  const filteredDisplayInventory = useMemo(() => {
    const query = productSearchQuery.toLowerCase();

    if (viewMode === 'all') {
      const newGroupedInventory = {};
      Object.entries(groupedInventory).forEach(([zoneName, { zone, items }]) => {
        const filteredItems = items.filter(item =>
          item.productName?.toLowerCase().includes(query) ||
          item.designer?.toLowerCase().includes(query) ||
          item.manufacturer?.toLowerCase().includes(query) ||
          item.material?.toLowerCase().includes(query) ||
          item.sku?.toLowerCase().includes(query)
        );
        if (filteredItems.length > 0) {
          newGroupedInventory[zoneName] = { zone, items: filteredItems };
        }
      });
      return newGroupedInventory;
    } else { // viewMode === 'zone'
      return inventory.filter(item =>
        item.productName?.toLowerCase().includes(query) ||
        item.designer?.toLowerCase().includes(query) ||
        item.manufacturer?.toLowerCase().includes(query) ||
        item.material?.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query)
      );
    }
  }, [inventory, groupedInventory, viewMode, productSearchQuery]);


  const renderTableRow = (item, index) => (
    <React.Fragment key={item.id}>
      {/* 1단: 이미지 + 기본정보 */}
      <tr className="hover:bg-gray-50 border-t border-gray-200">
        <td rowSpan={2} className="px-2 py-2 text-sm text-gray-600 text-center align-middle w-8">{index + 1}</td>
        <td rowSpan={2} className="px-2 py-2 align-middle w-20">
          {item.photos && item.photos.length > 0 ? (
            <img
              src={item.photos[0]}
              alt={item.productName}
              className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setImageModal({ show: true, src: item.photos[0], productName: item.productName })}
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
              <Icons.Image className="w-6 h-6 text-gray-300" />
            </div>
          )}
        </td>
        <td className="px-3 py-2">
          <button
            onClick={() => setProductDetailModal({ show: true, item })}
            className="font-bold text-gray-900 text-base hover:text-brand-600 hover:underline cursor-pointer uppercase text-left"
          >
            {item.productName || '-'}
          </button>
          {item.zoneSource === 'sku' && (
            <span className="ml-2 text-xs text-blue-600">SKU {item.skuCount}/{item.totalSkuCount}</span>
          )}
          {(item.designer || item.manufacturer) && (
            <div className="text-xs text-gray-500 mt-0.5">
              {item.designer && <span>{item.designer}</span>}
              {item.designer && item.manufacturer && <span className="mx-1">·</span>}
              {item.manufacturer && <span>{item.manufacturer}</span>}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap w-36">
          <div className="text-sm text-gray-500">{formatPrice(item.salePrice)} <span className="text-gray-400 text-xs">excl.</span></div>
          <div className="text-base font-bold text-brand-600">{formatPriceWithTax(item.salePrice)} <span className="text-brand-400 text-xs">incl.</span></div>
        </td>
        <td className="px-3 py-2 text-center w-16">
          <button
            onClick={(e) => handleInventoryClick(e, item)}
            className="text-brand-600 hover:text-brand-800 font-bold text-base hover:underline cursor-pointer"
          >
            {item.quantity || 1}
          </button>
        </td>
        <td className="px-3 py-2 text-sm text-gray-600 w-20">{item.ownershipType === '위탁' ? (item.consignorName || '-') : 'OOM'}</td>
      </tr>
      {/* 2단: 상세정보 */}
      <tr className="hover:bg-gray-50 border-b border-gray-100">
        <td colSpan={4} className="px-3 py-2">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
            {item.material && <span><span className="text-gray-400">재질:</span> {item.material}</span>}
            {item.era && <span><span className="text-gray-400">연대:</span> {item.era}</span>}
            {item.size && <span><span className="text-gray-400">사이즈:</span> {item.size}</span>}
            {item.notes && <span className="text-gray-500"><span className="text-gray-400">메모:</span> {item.notes}</span>}
          </div>
        </td>
      </tr>
    </React.Fragment>
  );

  const renderTableHeader = () => (
    <tr className="bg-gray-100 border-b-2 border-gray-300">
      <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 w-8">No</th>
      <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 w-16">사진</th>
      <th className="px-3 py-3 text-left text-sm font-bold text-gray-700">제품명</th>
      <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 w-28">가격</th>
      <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 w-12">재고</th>
      <th className="px-3 py-3 text-center text-sm font-bold text-gray-700 w-16">소유자</th>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  const totalItems = viewMode === 'all'
    ? Object.values(filteredDisplayInventory).reduce((sum, g) => sum + g.items.length, 0)
    : filteredDisplayInventory.length;

  return (
    <div className="w-full px-0">
      {/* 헤더 */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-2xl font-bold text-brand-900">Price List</h2>
        {totalItems > 0 && (
          <button
            onClick={() => setShowProductSelectionModal(true)}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg transition shadow-md flex items-center font-medium text-sm"
          >
            <Icons.FileText className="w-4 h-4 mr-1.5" />
            견적서 생성
          </button>
        )}
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-brand-100 p-1 mb-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-bold text-brand-900 mb-0.5">위치 선택</label>
            <select
              value={selectedLocation}
              onChange={(e) => {
                setSelectedLocation(e.target.value);
                setSelectedZone('');
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">전체 위치</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-brand-900 mb-0.5">Zone 선택</label>
            <select
              value={selectedZone}
              onChange={(e) => {
                const zoneId = e.target.value;
                setSelectedZone(zoneId);
                if (zoneId) {
                  setViewMode('zone');
                } else {
                  setViewMode('all');
                }
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">전체 보기</option>
              {filteredZones.map(zone => (
                <option key={zone.id} value={zone.id}>
                  {zone.locationName} - {zone.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-bold text-brand-900 mb-0.5">제품 검색</label>
            <input
              type="text"
              placeholder="제품명, 디자이너, 제조사 등으로 검색"
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        </div>
      </div>

      {/* 요약 정보 - 필터 아래에 표시 */}
      {!loadingInventory && viewMode === 'all' && Object.keys(groupedInventory).length > 0 && (
        <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 mb-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-700">
              총 {Object.keys(groupedInventory).length}개 Zone, {totalItems}개 제품
            </span>
            <span className="text-xs text-gray-500">
              {new Date().toLocaleDateString('ko-KR')} 기준
            </span>
          </div>
        </div>
      )}

      {!loadingInventory && viewMode === 'zone' && selectedZone && inventory.length > 0 && (
        <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 mb-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-700">
              총 {inventory.length}개 제품
            </span>
            <span className="text-xs text-gray-500">
              {new Date().toLocaleDateString('ko-KR')} 기준
            </span>
          </div>
        </div>
      )}

      {/* 로딩 */}
      {loadingInventory && (
        <div className="flex items-center justify-center py-20 bg-white rounded-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      )}

      {/* Zone 선택 안내 (zone 모드일 때만) */}
      {viewMode === 'zone' && !selectedZone && !loadingInventory && (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <Icons.MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Zone을 선택해주세요</h3>
          <p className="text-gray-500">가격표를 확인할 전시 구역을 선택하세요.</p>
        </div>
      )}

      {/* 전체 보기 모드 */}
      {viewMode === 'all' && !loadingInventory && (
        <div className="space-y-3">
          {Object.keys(filteredDisplayInventory).length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
              <Icons.Box className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">검색된 제품이 없습니다.</p>
            </div>
          ) : (
            Object.entries(filteredDisplayInventory).map(([zoneName, { zone, items }]) => (
              <div key={zoneName} className="bg-white rounded-lg shadow-sm border border-brand-100 overflow-hidden print:break-inside-avoid">
                <div className="bg-brand-800 text-white px-3 py-2">
                  <h3 className="text-base font-bold">{zoneName}</h3>
                  <p className="text-brand-200 text-xs">{items.length}개 제품</p>
                </div>
                <table className="w-full">
                  <thead>{renderTableHeader()}</thead>
                  <tbody>
                    {items.map((item, idx) => renderTableRow(item, idx))}
                  </tbody>
                </table>
              </div>
            ))
          )}

        </div>
      )}

      {/* Zone별 보기 모드 */}
      {viewMode === 'zone' && selectedZone && !loadingInventory && (
        <div className="bg-white rounded-lg shadow-sm border border-brand-100 overflow-hidden">
          <div className="bg-brand-900 text-white px-3 py-2 print:bg-black">
            <h3 className="text-base font-bold text-center">{selectedZoneName}</h3>
            <p className="text-center text-brand-200 text-xs print:text-gray-400">
              원오디너리맨션 전시 제품 가격표
            </p>
          </div>

          {filteredDisplayInventory.length === 0 ? (
            <div className="text-center py-16">
              <Icons.Box className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">이 Zone에 등록된 제품이 없습니다.</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead>{renderTableHeader()}</thead>
                <tbody>
                  {filteredDisplayInventory.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((item, idx) => renderTableRow(item, idx))}
                </tbody>
              </table>
              <div className="flex justify-center items-center gap-2 p-4 border-t border-gray-200">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-50"
                >
                  이전
                </button>
                <span className="text-sm font-medium">
                  {currentPage} / {Math.ceil(filteredDisplayInventory.length / itemsPerPage)}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredDisplayInventory.length / itemsPerPage), prev + 1))}
                  disabled={currentPage === Math.ceil(filteredDisplayInventory.length / itemsPerPage)}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-50"
                >
                  다음
                </button>
              </div>
            </>
          )}

        </div>
      )}

      {/* 제품 선택 모달 */}
      {showProductSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
            {/* 모달 헤더 */}
            <div className="bg-brand-900 text-white px-4 py-3 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">견적서 제품 선택</h3>
                <p className="text-brand-200 text-xs">견적서에 포함할 제품을 선택하세요</p>
              </div>
              <button
                onClick={() => setShowProductSelectionModal(false)}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            {/* 선택된 항목 카운트 */}
            <div className="px-4 py-2 bg-brand-50 border-b border-brand-100 flex justify-between items-center">
              <span className="text-sm text-brand-700">
                {selectedItems.size > 0 ? (
                  <span className="font-bold">{selectedItems.size}개 제품 선택됨</span>
                ) : (
                  '제품을 선택해주세요'
                )}
              </span>
              {selectedItems.size > 0 && (
                <button
                  onClick={() => setSelectedItems(new Set())}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  선택 초기화
                </button>
              )}
            </div>

            {/* 제품 리스트 */}
            <div className="flex-1 overflow-y-auto p-2">
              {Object.entries(filteredDisplayInventory).length === 0 ? (
                <div className="text-center py-16">
                  <Icons.Box className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">등록된 제품이 없습니다.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(filteredDisplayInventory).map(([zoneName, { zone, items }]) => (
                    <div key={zoneName} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-3 py-2 flex justify-between items-center">
                        <span className="font-bold text-sm text-gray-700">{zoneName}</span>
                        <button
                          onClick={() => {
                            const zoneItemIds = items.map(item => item.id);
                            const allSelected = zoneItemIds.every(id => selectedItems.has(id));
                            setSelectedItems(prev => {
                              const newSet = new Set(prev);
                              if (allSelected) {
                                zoneItemIds.forEach(id => newSet.delete(id));
                              } else {
                                zoneItemIds.forEach(id => newSet.add(id));
                              }
                              return newSet;
                            });
                          }}
                          className="px-2 py-1 text-sm text-brand-600 hover:text-brand-800 font-medium rounded-md"
                        >
                          {items.every(item => selectedItems.has(item.id)) ? '전체 해제' : '전체 선택'}
                        </button>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {items.map((item) => (
                          <label
                            key={item.id}
                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${selectedItems.has(item.id) ? 'bg-brand-50' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              className="w-5 h-5 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                            />
                            {item.photos && item.photos.length > 0 ? (
                              <img
                                src={item.photos[0]}
                                alt={item.productName}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                <Icons.Image className="w-5 h-5 text-gray-300" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.productName || '-'}</p>
                              <p className="text-xs text-gray-500">{item.designer || '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-600">{formatPrice(item.salePrice)}</p>
                              <p className="text-sm font-bold text-brand-600">{formatPriceWithTax(item.salePrice)}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setShowProductSelectionModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                취소
              </button>
              <button
                onClick={() => {
                  if (selectedItems.size === 0) {
                    alert('견적서에 포함할 제품을 선택해주세요.');
                    return;
                  }
                  // 선택된 아이템들의 수량 초기화
                  const selectedData = getSelectedItemsData();
                  const initialQuantities = {};
                  selectedData.forEach(item => {
                    initialQuantities[item.id] = item.quantity || 1;
                  });
                  setQuotationQuantities(initialQuantities);
                  setDiscountValue(0);
                  setShowProductSelectionModal(false);
                  setShowQuotationModal(true);
                }}
                disabled={selectedItems.size === 0}
                className={`px-6 py-2 rounded-lg transition-colors font-medium text-sm flex items-center ${
                  selectedItems.size === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 text-white'
                }`}
              >
                <Icons.FileText className="w-4 h-4 mr-1.5" />
                견적서 생성 ({selectedItems.size}개)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 견적서 모달 */}
      {showQuotationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* 모달 헤더 */}
            <div className="bg-brand-900 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">견적서 생성</h3>
              <button
                onClick={() => {
                  setShowQuotationModal(false);
                  setSelectedCustomer(null);
                  setShowCustomerAddForm(false);
                }}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            {/* 모달 내용 */}
            <div className="flex-1 overflow-y-auto">
              {/* 거래처 선택/등록 */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-bold text-gray-700">거래처 선택 (선택사항)</label>
                  <button
                    onClick={() => setShowCustomerAddForm(!showCustomerAddForm)}
                    className="text-sm text-brand-600 hover:text-brand-700 font-medium flex items-center"
                  >
                    <Icons.Plus className="w-4 h-4 mr-1" />
                    {showCustomerAddForm ? '취소' : '새 거래처 등록'}
                  </button>
                </div>

                {showCustomerAddForm ? (
                                      <div className="bg-white p-4 rounded-lg border border-gray-200 mt-3">
                                      <h4 className="font-bold text-gray-700 mb-4">새 거래처 등록</h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <label className="block text-sm font-semibold text-gray-700 mb-1">거래처명 <span className="text-red-500">*</span></label>
                                          <input
                                            type="text"
                                            value={newCustomerData.name}
                                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="거래처명 입력"
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm font-semibold text-gray-700 mb-1">종류</label>
                                          <select
                                            value={newCustomerData.type}
                                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, type: e.target.value }))}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                          >
                                            <option value="고객">고객</option>
                                            <option value="판매">판매</option>
                                            <option value="대량">대량</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-sm font-semibold text-gray-700 mb-1">연락처</label>
                                          <input
                                            type="tel"
                                            value={newCustomerData.contact}
                                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, contact: e.target.value }))}
                                            placeholder="010-1234-5678"
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm font-semibold text-gray-700 mb-1">이메일</label>
                                          <input
                                            type="email"
                                            value={newCustomerData.email}
                                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="example@email.com"
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                          />
                                        </div>
                                        <div className="md:col-span-2">
                                          <label className="block text-sm font-semibold text-gray-700 mb-1">주소</label>
                                          <input
                                            type="text"
                                            value={newCustomerData.address}
                                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, address: e.target.value }))}
                                            placeholder="주소 입력"
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                          />
                                        </div>
                                        <div className="md:col-span-2">
                                          <label className="block text-sm font-semibold text-gray-700 mb-1">메모</label>
                                          <input
                                            type="text"
                                            value={newCustomerData.notes}
                                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="메모 입력"
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                          />
                                        </div>
                                      </div>
                                      <div className="flex justify-end mt-4">
                                        <button
                                          onClick={handleAddCustomer}
                                          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors font-medium"
                                        >
                                          거래처 등록
                                        </button>
                                      </div>
                                    </div>                ) : (
                  <select
                    value={selectedCustomer?.id || ''}
                    onChange={(e) => {
                      const customer = customers.find(c => c.id === e.target.value);
                      setSelectedCustomer(customer || null);
                    }}
                    className="w-full md:w-1/2 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">거래처 없음</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} {customer.type ? `(${customer.type})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* 할인 설정 */}
              <div className="p-6 border-b border-gray-200 bg-white">
                <label className="block text-sm font-semibold text-gray-700 mb-3">할인 설정</label>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <select
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value="percent">% 할인</option>
                      <option value="amount">금액 할인</option>
                    </select>
                    <input
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(Math.max(0, Number(e.target.value)))}
                      placeholder={discountType === 'percent' ? '할인율' : '할인금액'}
                      className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      min="0"
                      max={discountType === 'percent' ? 100 : undefined}
                    />
                    <span className="text-gray-500">{discountType === 'percent' ? '%' : '원'}</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showDiscountInQuotation}
                      onChange={(e) => setShowDiscountInQuotation(e.target.checked)}
                      className="w-4 h-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-600">견적서에 할인 표시</span>
                  </label>
                </div>
                {discountValue > 0 && (
                  <p className="mt-2 text-sm text-brand-600">
                    할인 금액: {formatPrice(calculateTotals().discountAmount)}원
                    {!showDiscountInQuotation && <span className="text-gray-500"> (견적서에 미표시)</span>}
                  </p>
                )}
              </div>

              {/* 견적서 미리보기 */}
              <div ref={quotationRef} className="p-6">
                {/* 견적서 헤더 */}
                <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">견 적 서</h1>
                  <p className="text-gray-500">{new Date().toLocaleDateString('ko-KR')}</p>
                </div>

                {/* 거래처 정보 */}
                {selectedCustomer && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-gray-700 mb-2">거래처 정보</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">거래처명:</span> <span className="font-medium">{selectedCustomer.name}</span></div>
                      {selectedCustomer.contact && <div><span className="text-gray-500">연락처:</span> <span className="font-medium">{selectedCustomer.contact}</span></div>}
                      {selectedCustomer.email && <div><span className="text-gray-500">이메일:</span> <span className="font-medium">{selectedCustomer.email}</span></div>}
                      {selectedCustomer.address && <div className="col-span-2"><span className="text-gray-500">주소:</span> <span className="font-medium">{selectedCustomer.address}</span></div>}
                    </div>
                  </div>
                )}

                {/* 공급자 정보 */}
                <div className="mb-6 p-4 bg-brand-50 rounded-lg">
                  <h4 className="font-bold text-gray-700 mb-2">공급자 정보</h4>
                  <div className="text-sm">
                    <div><span className="text-gray-500">상호:</span> <span className="font-medium">원오디너리맨션</span></div>
                  </div>
                </div>

                {/* 제품 목록 */}
                <div className="mb-6">
                  <h4 className="font-bold text-gray-700 mb-3">제품 내역</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-center border-b">No.</th>
                          <th className="px-3 py-2 text-left border-b">제품명</th>
                          <th className="px-3 py-2 text-left border-b">디자이너</th>
                          <th className="px-3 py-2 text-left border-b">사이즈</th>
                          <th className="px-3 py-2 text-center border-b print:hidden">수량</th>
                          <th className="px-3 py-2 text-center border-b hidden print:table-cell">수량</th>
                          <th className="px-3 py-2 text-right border-b">단가(VAT별도)</th>
                          <th className="px-3 py-2 text-right border-b">금액(VAT별도)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSelectedItemsData().map((item, index) => {
                          const qty = quotationQuantities[item.id] || item.quantity || 1;
                          return (
                            <tr key={item.id} className="border-b border-gray-100">
                              <td className="px-3 py-2 text-center">{index + 1}</td>
                              <td className="px-3 py-2">{item.productName || '-'}</td>
                              <td className="px-3 py-2">{item.designer || '-'}</td>
                              <td className="px-3 py-2">{item.size || '-'}</td>
                              <td className="px-3 py-2.5 text-center print:hidden">
                                <input
                                  type="number"
                                  value={qty}
                                  onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                  min="1"
                                  className="w-20 px-3 py-1.5 border border-gray-300 rounded text-center focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base"
                                />
                              </td>
                              <td className="px-3 py-2.5 text-center hidden print:table-cell">{qty}</td>
                              <td className="px-3 py-2.5 text-right">{formatPrice(item.salePrice)}원</td>
                              <td className="px-3 py-2.5 text-right">{formatPrice((item.salePrice || 0) * qty)}원</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 합계 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-end space-y-2 flex-col items-end">
                    <div className="flex justify-between w-full md:w-72 text-sm">
                      <span className="text-gray-600">품목 수:</span>
                      <span className="font-medium">{calculateTotals().itemCount}개</span>
                    </div>
                    <div className="flex justify-between w-full md:w-72 text-sm">
                      <span className="text-gray-600">총 수량:</span>
                      <span className="font-medium">{calculateTotals().totalQuantity}개</span>
                    </div>
                    <div className="flex justify-between w-full md:w-72 text-sm">
                      <span className="text-gray-600">소계 (VAT별도):</span>
                      <span className="font-medium">{formatPrice(calculateTotals().subtotalExclVat)}원</span>
                    </div>
                    {showDiscountInQuotation && calculateTotals().discountAmount > 0 && (
                      <div className="flex justify-between w-full md:w-72 text-sm text-red-600">
                        <span>할인 ({discountType === 'percent' ? `${discountValue}%` : `${formatPrice(discountValue)}원`}):</span>
                        <span className="font-medium">-{formatPrice(calculateTotals().discountAmount)}원</span>
                      </div>
                    )}
                    <div className="flex justify-between w-full md:w-72 text-sm">
                      <span className="text-gray-600">공급가액 (VAT별도):</span>
                      <span className="font-medium">{formatPrice(calculateTotals().totalExclVat)}원</span>
                    </div>
                    <div className="flex justify-between w-full md:w-72 text-sm">
                      <span className="text-gray-600">부가세 (10%):</span>
                      <span className="font-medium">{formatPrice(calculateTotals().totalInclVat - calculateTotals().totalExclVat)}원</span>
                    </div>
                    <div className="flex justify-between w-full md:w-72 pt-2 border-t border-gray-300">
                      <span className="font-bold text-gray-900">합계 (VAT포함):</span>
                      <span className="font-bold text-brand-600 text-lg">{formatPrice(calculateTotals().totalInclVat)}원</span>
                    </div>
                  </div>
                </div>

                {/* 비고 */}
                <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-bold text-gray-700 mb-2">비고</h4>
                  <p className="text-sm text-gray-500">
                    • 상기 금액은 견적 금액이며, 실제 거래 시 변동될 수 있습니다.<br />
                    • 견적 유효기간: 발행일로부터 30일
                  </p>
                </div>
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowQuotationModal(false);
                  setSelectedCustomer(null);
                  setShowCustomerAddForm(false);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={handleExcelDownload}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center"
              >
                <Icons.Excel className="w-5 h-5 mr-2" />
                엑셀 다운로드
              </button>
              <button
                onClick={handlePrintQuotation}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors font-medium flex items-center"
              >
                <Icons.Printer className="w-5 h-5 mr-2" />
                인쇄
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 재고 위치별 팝업 */}
      {inventoryPopup.show && inventoryPopup.item && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setInventoryPopup({ show: false, item: null, position: { x: 0, y: 0 } })}
          />
          <div
            className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-5 min-w-[200px]"
            style={{
              left: Math.min(inventoryPopup.position.x, window.innerWidth - 220),
              top: Math.min(inventoryPopup.position.y, window.innerHeight - 200),
            }}
          >
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
              <h4 className="font-bold text-gray-900 text-lg">위치별 재고</h4>
              <button
                onClick={() => setInventoryPopup({ show: false, item: null, position: { x: 0, y: 0 } })}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-3">
              {getLocationInventory(inventoryPopup.item).map((loc, idx) => (
                <div key={idx} className="flex justify-between items-center text-base">
                  <span className="text-gray-600">{loc.location}</span>
                  <span className="font-bold text-brand-600 text-lg">{loc.count}개</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center text-base">
              <span className="font-medium text-gray-700">총 재고</span>
              <span className="font-bold text-gray-900 text-lg">{inventoryPopup.item.quantity || 1}개</span>
            </div>
          </div>
        </>
      )}

      {/* 제품 상세 모달 */}
      {productDetailModal.show && productDetailModal.item && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setProductDetailModal({ show: false, item: null })}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div className="bg-brand-900 text-white px-5 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold">제품 정보</h3>
              <button
                onClick={() => setProductDetailModal({ show: false, item: null })}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            {/* 모달 내용 */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* 제품 이미지 */}
              {productDetailModal.item.photos && productDetailModal.item.photos.length > 0 ? (
                <div className="mb-4">
                  <img
                    src={productDetailModal.item.photos[0]}
                    alt={productDetailModal.item.productName}
                    className="w-full h-64 object-contain bg-gray-100 rounded-lg cursor-pointer"
                    onClick={() => {
                      setImageModal({
                        show: true,
                        src: productDetailModal.item.photos[0],
                        productName: productDetailModal.item.productName
                      });
                    }}
                  />
                  {productDetailModal.item.photos.length > 1 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto">
                      {productDetailModal.item.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt={`${productDetailModal.item.productName} ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => setImageModal({ show: true, src: photo, productName: productDetailModal.item.productName })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <Icons.Image className="w-16 h-16 text-gray-300" />
                </div>
              )}

              {/* 제품명 */}
              <h4 className="text-xl font-bold text-gray-900 uppercase mb-1">
                {productDetailModal.item.productName || '-'}
              </h4>

              {/* 디자이너 / 제조사 */}
              {(productDetailModal.item.designer || productDetailModal.item.manufacturer) && (
                <p className="text-sm text-gray-600 mb-4">
                  {productDetailModal.item.designer}
                  {productDetailModal.item.designer && productDetailModal.item.manufacturer && ' · '}
                  {productDetailModal.item.manufacturer}
                </p>
              )}

              {/* 가격 정보 */}
              <div className="bg-brand-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">판매가 (VAT 별도)</span>
                  <span className="text-lg font-medium">{formatPrice(productDetailModal.item.salePrice)}원</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">판매가 (VAT 포함)</span>
                  <span className="text-xl font-bold text-brand-600">{formatPriceWithTax(productDetailModal.item.salePrice)}원</span>
                </div>
              </div>

              {/* 상세 정보 */}
              <div className="space-y-2 text-sm">
                {productDetailModal.item.material && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">재질</span>
                    <span className="text-gray-900">{productDetailModal.item.material}</span>
                  </div>
                )}
                {productDetailModal.item.era && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">연대</span>
                    <span className="text-gray-900">{productDetailModal.item.era}</span>
                  </div>
                )}
                {productDetailModal.item.size && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">사이즈</span>
                    <span className="text-gray-900">{productDetailModal.item.size}</span>
                  </div>
                )}
                {productDetailModal.item.condition && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">컨디션</span>
                    <span className="text-gray-900">{productDetailModal.item.condition}</span>
                  </div>
                )}
                {productDetailModal.item.quantity && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">재고</span>
                    <span className="text-gray-900 font-medium">{productDetailModal.item.quantity}개</span>
                  </div>
                )}
                {productDetailModal.item.notes && (
                  <div className="py-2">
                    <span className="text-gray-500 block mb-1">메모</span>
                    <span className="text-gray-900">{productDetailModal.item.notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => setProductDetailModal({ show: false, item: null })}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                닫기
              </button>
              <button
                onClick={() => {
                  navigate(`/inventory/${productDetailModal.item.id}`);
                  setProductDetailModal({ show: false, item: null });
                }}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors font-medium flex items-center"
              >
                <Icons.ExternalLink className="w-4 h-4 mr-1.5" />
                상세 페이지로 이동
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 확대 모달 */}
      {imageModal.show && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setImageModal({ show: false, src: '', productName: '' })}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setImageModal({ show: false, src: '', productName: '' })}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            >
              <Icons.Close className="w-8 h-8" />
            </button>
            <img
              src={imageModal.src}
              alt={imageModal.productName}
              className="w-full h-full object-contain rounded-lg"
            />
            {imageModal.productName && (
              <p className="text-center text-white mt-4 text-lg font-medium">
                {imageModal.productName}
              </p>
            )}
          </div>
        </div>
      )}

      {/* 인쇄 스타일 */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-area, #print-area * {
            visibility: visible;
          }
          #print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          table {
            font-size: 10px;
          }
          th, td {
            padding: 4px 6px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ZonePriceList;
